import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { fetchMembershipAsOf, previewMembershipOverride, saveMembershipOverride } from '../api/membershipApi';
import type { MembershipAsOf, MembershipPreview } from '../types';
import { localTodayIso } from '../utils/membershipFormat';

interface Props {
  show: boolean;
  apiPath: string;
  onHide: () => void;
  onSaved: () => void;
}

const SetMembershipCountModal: React.FC<Props> = ({ show, apiPath, onHide, onSaved }) => {
  const today = localTodayIso();
  const [date, setDate] = useState(today);
  const [asOf, setAsOf] = useState<MembershipAsOf | null>(null);
  const [total, setTotal] = useState(0);
  const [male, setMale] = useState(0);
  const [female, setFemale] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<MembershipPreview | null>(null);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    fetchMembershipAsOf(apiPath, date).then((response) => {
      if (cancelled) return;
      if (!response.isSuccess() || !response.getData()) {
        setError(response.getError()?.detail ?? 'Please reload and try again.');
        return;
      }
      const data = response.getData()!;
      setAsOf(data);
      setTotal(data.total_members);
      setMale(data.male_count);
      setFemale(data.female_count);
    });
    return () => {
      cancelled = true;
    };
  }, [show, apiPath, date]);

  const payload = {
    effective_date: date,
    last_membership_entry_id: asOf?.last_membership_entry_id ?? null,
    total_members: total,
    male_count: male,
    female_count: female,
  };

  const review = async () => {
    setBusy(true);
    setError(null);
    const response = await previewMembershipOverride(apiPath, payload);
    setBusy(false);
    if (!response.isSuccess() || !response.getData()) {
      setError(response.getError()?.detail ?? 'Please check the figures and try again.');
      return;
    }
    setPreview(response.getData());
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    const response = await saveMembershipOverride(apiPath, payload);
    setBusy(false);
    if (!response.isSuccess()) {
      setError(response.getError()?.detail ?? 'Please reload and try again.');
      return;
    }
    setPreview(null);
    onSaved();
    onHide();
  };

  const bannerTotal = asOf?.total_members ?? 0;
  const bannerMale = asOf?.male_count ?? 0;
  const bannerFemale = asOf?.female_count ?? 0;

  return (
    <>
      <Modal show={show && !preview} onHide={onHide} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Set membership count</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="warning">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Count as of</Form.Label>
            <Form.Control type="date" max={today} value={date} onChange={(event) => setDate(event.target.value)} />
          </Form.Group>
          <Alert variant="warning">
            This replaces the total of {bannerTotal.toLocaleString()} (Male {bannerMale.toLocaleString()}, Female {bannerFemale.toLocaleString()}) from this date onward. Use it to correct the count.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Total members</Form.Label>
            <Form.Control type="number" min={0} value={total} onChange={(event) => setTotal(Number(event.target.value))} />
          </Form.Group>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Label>Male</Form.Label>
              <Form.Control type="number" min={0} value={male} onChange={(event) => setMale(Number(event.target.value))} />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label>Female</Form.Label>
              <Form.Control type="number" min={0} value={female} onChange={(event) => setFemale(Number(event.target.value))} />
            </Col>
          </Row>
          <p className="small mb-0 mt-2">Male and Female must add up to the total.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" onClick={review} disabled={busy}>
            {busy ? <Spinner size="sm" animation="border" /> : 'Review'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(preview)} onHide={() => setPreview(null)} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Confirm membership count</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="warning">{error}</Alert>}
          <p>Please check this count before it is saved.</p>
          {preview && (
            <>
              <dl className="row mb-0">
                <dt className="col-6">Count as of</dt>
                <dd className="col-6">{preview.effective_date_label}</dd>
                <dt className="col-6">Total members</dt>
                <dd className="col-6">{preview.as_of_total.toLocaleString()} → {preview.new_total.toLocaleString()}</dd>
                <dt className="col-6">Male</dt>
                <dd className="col-6">{preview.as_of_male.toLocaleString()} → {preview.male_count.toLocaleString()}</dd>
                <dt className="col-6">Female</dt>
                <dd className="col-6">{preview.as_of_female.toLocaleString()} → {preview.female_count.toLocaleString()}</dd>
              </dl>
              <p className="mt-3">This replaces the count from this date onward. It does not add to or subtract from it.</p>
              {preview.later_message && <p className="mb-0">{preview.later_message}</p>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPreview(null)}>Back</Button>
          <Button variant="primary" onClick={confirm} disabled={busy}>Replace count</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SetMembershipCountModal;
